import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  KpiCard — Performance dashboard metric card                       */
/* ------------------------------------------------------------------ */

export interface KpiCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Metric label (e.g. "Revenue", "Occupancy"). */
  label: string;
  /** Formatted display value (e.g. "$1,550", "42.3%"). */
  value: string;
  /** Percentage change vs comparison period. Positive = green, negative = red. */
  delta?: number | null;
  /** Label for the delta (e.g. "vs last year"). */
  deltaLabel?: string;
  /** Sparkline data points (array of numbers, rendered as inline SVG). */
  sparklineData?: number[];
  /** Sparkline color override. Defaults to --sf-primary. */
  sparklineColor?: string;
  /** Whether the card is in a loading state. */
  loading?: boolean;
}

const css = /* css */ `
  .sf-kpi-card {
    background: var(--sf-bg-elevated);
    border: 1px solid var(--sf-border);
    border-radius: var(--sf-radius-lg);
    padding: var(--sf-space-5);
    display: flex;
    flex-direction: column;
    gap: var(--sf-space-2);
    min-width: 0;
    transition: box-shadow var(--sf-transition-base), border-color var(--sf-transition-base);
    cursor: default;
  }

  .sf-kpi-card:hover {
    border-color: var(--sf-border-strong);
    box-shadow: var(--sf-shadow-md);
  }

  .sf-kpi-card--clickable {
    cursor: pointer;
  }

  .sf-kpi-card__label {
    font-size: var(--sf-text-sm);
    font-weight: var(--sf-weight-medium);
    color: var(--sf-text-muted);
    line-height: var(--sf-leading-tight);
    letter-spacing: 0.01em;
  }

  .sf-kpi-card__value {
    font-size: var(--sf-text-xl);
    font-weight: var(--sf-weight-bold);
    color: var(--sf-text);
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  .sf-kpi-card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sf-space-2);
    min-height: 24px;
    flex-wrap: wrap;
  }

  .sf-kpi-card__delta {
    display: inline-flex;
    align-items: center;
    gap: var(--sf-space-1);
    font-size: var(--sf-text-xs);
    font-weight: var(--sf-weight-medium);
    line-height: 1;
    padding: 2px 6px;
    border-radius: var(--sf-radius-sm);
    white-space: nowrap;
  }

  .sf-kpi-card__delta--positive {
    color: var(--sf-success);
    background: var(--sf-success-light);
  }

  .sf-kpi-card__delta--negative {
    color: var(--sf-error);
    background: var(--sf-error-light);
  }

  .sf-kpi-card__delta--neutral {
    color: var(--sf-text-muted);
    background: var(--sf-bg-inset);
  }

  .sf-kpi-card__delta-label {
    font-size: var(--sf-text-xs);
    color: var(--sf-text-muted);
    margin-left: var(--sf-space-1);
    white-space: nowrap;
  }

  .sf-kpi-card__skeleton-value {
    height: 28px;
    width: 80px;
    background: var(--sf-bg-inset);
    border-radius: var(--sf-radius-sm);
    animation: sf-kpi-pulse 1.5s ease-in-out infinite;
  }

  .sf-kpi-card__skeleton-delta {
    height: 18px;
    width: 50px;
    background: var(--sf-bg-inset);
    border-radius: var(--sf-radius-sm);
    animation: sf-kpi-pulse 1.5s ease-in-out 0.2s infinite;
  }

  @keyframes sf-kpi-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

function renderSparkline(data: number[], color: string, width = 80, height = 28) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padding = 2;
  const effectiveHeight = height - padding * 2;
  const step = (width - padding * 2) / (data.length - 1);
  const points = data
    .map((v, i) => `${padding + i * step},${padding + effectiveHeight - ((v - min) / range) * effectiveHeight}`)
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      style={{ display: "block", flexShrink: 0 }}
    >
      <polyline
        points={points}
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function renderDeltaArrow(positive: boolean) {
  if (positive) {
    return (
      <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
        <path d="M5 2L8 6H2L5 2Z" />
      </svg>
    );
  }
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
      <path d="M5 8L2 4H8L5 8Z" />
    </svg>
  );
}

const KpiCard = React.forwardRef<HTMLDivElement, KpiCardProps>(
  (
    {
      label,
      value,
      delta,
      deltaLabel,
      sparklineData,
      sparklineColor,
      loading = false,
      onClick,
      className,
      ...rest
    },
    ref,
  ) => {
    injectStyles("sf-kpi-card", css);

    const classes = [
      "sf-kpi-card",
      onClick ? "sf-kpi-card--clickable" : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    const deltaVariant =
      delta == null || delta === 0
        ? "neutral"
        : delta > 0
          ? "positive"
          : "negative";

    return (
      <div
        ref={ref}
        className={classes}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
                }
              }
            : undefined
        }
        {...rest}
      >
        <div className="sf-kpi-card__label">{label}</div>
        {loading ? (
          <>
            <div className="sf-kpi-card__skeleton-value" />
            <div className="sf-kpi-card__footer">
              <div className="sf-kpi-card__skeleton-delta" />
            </div>
          </>
        ) : (
          <>
            <div className="sf-kpi-card__value">{value}</div>
            <div className="sf-kpi-card__footer">
              <div>
                {delta != null && (
                  <span className={`sf-kpi-card__delta sf-kpi-card__delta--${deltaVariant}`}>
                    {renderDeltaArrow(delta >= 0)}
                    {delta >= 0 ? "+" : ""}
                    {delta.toFixed(1)}%
                    {deltaLabel && (
                      <span className="sf-kpi-card__delta-label">{deltaLabel}</span>
                    )}
                  </span>
                )}
              </div>
              {sparklineData && sparklineData.length >= 2 && (
                renderSparkline(
                  sparklineData,
                  sparklineColor || "var(--sf-primary)",
                )
              )}
            </div>
          </>
        )}
      </div>
    );
  },
);

KpiCard.displayName = "KpiCard";
export { KpiCard };
